"use client";

import { LearningRichContent } from "@/components/student/LearningRichContent";
import {
  LessonSectionFrame,
  lessonSectionBodyGapClass,
  lessonSectionBodyProseClass,
  stripSectionNumbering,
} from "@/components/student/LessonSectionFrame";
import { PagedContentSections, type ContentPage } from "@/components/student/PagedContentSections";

/**
 * The minimal legacy content shape written by canonical/proactive/remedial generation
 * services when they don't produce richer structured data:
 *   { sections: [{ heading, content }] }
 * This shape previously matched none of the `isXStructuredData` guards, so it fell
 * through to the "Content format issue. Showing raw content." fallback even though it's
 * a well-formed, known shape.
 */
export type LegacySection = {
  heading?: string;
  content: string;
};

export type LegacySectionsData = {
  sections: LegacySection[];
};

export function isLegacySectionsData(v: unknown): v is LegacySectionsData {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.sections) || o.sections.length === 0) return false;
  return o.sections.every((s) => {
    if (typeof s !== "object" || s === null) return false;
    const sec = s as Record<string, unknown>;
    return typeof sec.content === "string";
  });
}

export default function LegacySectionsLessonContent({ data }: { data: LegacySectionsData }) {
  const pages: ContentPage[] = data.sections.map((section, index) => {
    const heading = section.heading ? stripSectionNumbering(section.heading) : "";
    return {
      id: index,
      label: heading || `Part ${index + 1}`,
      node: (
        <LessonSectionFrame sectionNumber={index + 1} heading={heading}>
          <LearningRichContent
            content={section.content || ""}
            className={lessonSectionBodyProseClass}
            segmentGapClassName={lessonSectionBodyGapClass}
          />
        </LessonSectionFrame>
      ),
    };
  });

  return (
    <article className="learning-content max-w-none prose prose-gray max-w-none">
      <PagedContentSections pages={pages} />
    </article>
  );
}
